import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, RefreshCw, LogOut, MessageCircle, Inbox } from "lucide-react";
import { Reveal } from "../components/motion";
import { waLink } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["New", "Contacted", "Quoted", "Won"];
const STATUS_STYLES = {
  New: "bg-steel/10 text-steel border-steel/30",
  Contacted: "bg-amber-50 text-amber-700 border-amber-200",
  Quoted: "bg-violet-50 text-violet-700 border-violet-200",
  Won: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("sv_admin_key") || "");
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("board");

  const load = useCallback(async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/quotes`, { headers: { "X-Admin-Key": k } });
      setQuotes(res.data);
      localStorage.setItem("sv_admin_key", k);
    } catch {
      setError("Invalid admin key. Access denied.");
      setQuotes(null);
      localStorage.removeItem("sv_admin_key");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("sv_admin_key");
    if (saved) load(saved);
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}`, { status }, { headers: { "X-Admin-Key": key } });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch {
      setError("Could not update status. Please re-login and try again.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-steel focus:ring-2 focus:ring-steel/15";

  if (quotes === null) {
    return (
      <div className="min-h-screen bg-mist flex items-center justify-center px-4 pt-20" data-testid="admin-login">
        <Reveal className="w-full max-w-md">
          <div className="rounded-3xl border border-line bg-white p-8 sm:p-10 shadow-xl shadow-slate-200/60">
            <span className="w-12 h-12 rounded-xl bg-navy grid place-items-center mb-6">
              <Lock className="w-5 h-5 text-saffron" />
            </span>
            <h1 className="font-display text-2xl font-bold text-navy">Engagements</h1>
            <p className="mt-1 text-sm text-slate-600 mb-7">Enter the admin key to view quote requests and enquiries.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                load(key);
              }}
              className="space-y-4"
            >
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin key"
                className={inputCls}
                data-testid="admin-key-input"
              />
              {error && <p className="text-sm text-red-600" data-testid="admin-error">{error}</p>}
              <button
                type="submit"
                disabled={loading || !key}
                data-testid="admin-login-button"
                className="w-full rounded-full bg-saffron hover:bg-saffrondark disabled:opacity-60 text-white font-semibold py-3.5 transition-colors"
              >
                {loading ? "Checking..." : "View Engagements"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist pt-28 pb-20 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-2">SAARTHVA INTERNAL</p>
            <h1 className="font-display text-3xl font-bold text-navy">Engagements</h1>
            <p className="text-sm text-slate-600 mt-1" data-testid="admin-count">{quotes.length} request{quotes.length === 1 ? "" : "s"} received</p>
          </div>
          <div className="flex gap-3">
            <div className="flex rounded-full border border-line bg-white p-1" data-testid="view-toggle">
              {["board", "list"].map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  data-testid={`view-${v}-button`}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${
                    view === v ? "bg-navy text-white" : "text-slate-500 hover:text-navy"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/quotes/export.csv`, { headers: { "X-Admin-Key": key }, responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "saarthva-engagements.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  setError("Export failed. Please re-login and try again.");
                }
              }}
              data-testid="admin-export-csv-button"
              className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:border-saffron transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={() => load(key)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:border-steel transition-colors"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={() => { localStorage.removeItem("sv_admin_key"); setQuotes(null); setKey(""); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-saffron transition-colors"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        {view === "board" && quotes.length > 0 && (
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="pipeline-board">
            {STATUSES.map((st) => {
              const col = quotes.filter((q) => (q.status || "New") === st);
              return (
                <div key={st} className="rounded-2xl bg-white/60 border border-line p-3" data-testid={`pipeline-col-${st.toLowerCase()}`}>
                  <div className="flex items-center justify-between px-2 py-1 mb-3">
                    <span className={`rounded-full border text-xs font-semibold px-3 py-1 ${STATUS_STYLES[st]}`}>{st}</span>
                    <span className="text-xs font-bold text-slate-400" data-testid={`pipeline-count-${st.toLowerCase()}`}>{col.length}</span>
                  </div>
                  <div className="space-y-3">
                    {col.map((q) => (
                      <div key={q.id} className="rounded-xl border border-line bg-white p-4 transition-shadow hover:shadow-md" data-testid={`pipeline-card-${q.id}`}>
                        <p className="font-display font-bold text-navy text-sm">{q.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{q.company || "—"} · {q.city || "—"}</p>
                        <p className="mt-1.5 text-xs text-slate-600 line-clamp-2">{q.requirement_type || q.requirement}</p>
                        <p className="mt-1 text-[10px] text-slate-400">
                          {new Date(q.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {STATUSES.filter((s) => s !== (q.status || "New")).map((s) => (
                            <button
                              key={s}
                              onClick={() => setStatus(q.id, s)}
                              data-testid={`pipeline-move-${s.toLowerCase()}-${q.id}`}
                              className="rounded-full border border-line bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-500 hover:border-steel hover:text-navy transition-colors"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {col.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No leads</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {(view === "list" || quotes.length === 0) && (quotes.length === 0 ? (
          <div className="rounded-3xl border border-line bg-white p-16 text-center" data-testid="admin-empty">
            <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="font-display text-xl font-bold text-navy">No requests yet</p>
            <p className="text-sm text-slate-600 mt-1">New quote requests and enquiries will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((q) => (
              <div key={q.id} className="rounded-2xl border border-line bg-white p-6 sm:p-7" data-testid={`admin-quote-${q.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-display text-lg font-bold text-navy">
                      {q.name} <span className="text-slate-400 font-normal">· {q.company || "—"}</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      {" · "}{q.city || "—"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border text-xs font-semibold px-3 py-1.5 ${STATUS_STYLES[q.status] || STATUS_STYLES.New}`} data-testid={`admin-quote-status-${q.id}`}>
                      {q.status || "New"}
                    </span>
                    <span className="rounded-full bg-cream text-saffrondark text-xs font-semibold px-3 py-1.5">{q.requirement_type || q.requirement}</span>
                    <span className="rounded-full bg-mist border border-line text-slate-600 text-xs font-semibold px-3 py-1.5">via {q.contact_method}</span>
                  </div>
                </div>
                {q.details && <p className="mt-4 text-sm text-slate-600 leading-relaxed">{q.details}</p>}
                {(q.designation || q.locations || q.timeline || q.approx_spend) && (
                  <p className="mt-2 text-xs text-slate-500">
                    {[q.designation, q.locations && `${q.locations} location(s)`, q.timeline && `Timeline: ${q.timeline}`, q.approx_spend && `Approx: ${q.approx_spend}`].filter(Boolean).join(" · ")}
                  </p>
                )}
                {q.attachment_path && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/files/${q.attachment_path}`, { headers: { "X-Admin-Key": key }, responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = q.attachment_name || "attachment";
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        setError("Attachment download failed.");
                      }
                    }}
                    data-testid={`admin-quote-attachment-${q.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-steel hover:text-navy transition-colors"
                  >
                    View attachment{q.attachment_name ? `: ${q.attachment_name}` : ""}
                  </button>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                  <a href={`tel:${q.phone}`} className="font-semibold text-steel hover:text-navy" data-testid={`admin-quote-phone-${q.id}`}>{q.phone}</a>
                  <a href={`mailto:${q.email}`} className="font-semibold text-steel hover:text-navy" data-testid={`admin-quote-email-${q.id}`}>{q.email}</a>
                  <a
                    href={waLink(`Hi ${q.name}, this is Saarthva following up on your ${q.requirement} request.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-semibold text-[#128C4B] hover:text-navy"
                    data-testid={`admin-quote-whatsapp-${q.id}`}
                  >
                    <MessageCircle className="w-4 h-4" /> Reply on WhatsApp
                  </a>
                </div>
                <div className="mt-5 pt-4 border-t border-line flex flex-wrap items-center gap-2" data-testid={`admin-status-row-${q.id}`}>
                  <span className="text-xs font-semibold text-slate-500 mr-1">Mark as:</span>
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(q.id, s)}
                      disabled={(q.status || "New") === s}
                      data-testid={`admin-status-${s.toLowerCase()}-${q.id}`}
                      className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                        (q.status || "New") === s
                          ? "bg-navy text-white border-navy cursor-default"
                          : "bg-white text-slate-600 border-line hover:border-steel"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
